// The preferences picker's form model: the server's values <-> the editable draft, the
// validation that runs before a save, and the short summary lines the rail shows. Pure and
// unit-tested (preferences-form.test.ts). The server re-validates everything; this exists
// so a talent sees what is wrong next to the field instead of as a failed save.

import type { SellerPreferenceValues, SellerPreferencesUpdate } from "@recruiter/shared";

import { formatHour, formatUsd } from "./format";

/** ISO weekdays, Monday first, with the labels the day toggles and summaries use. */
export const WEEKDAYS: ReadonlyArray<{ day: number; short: string; letter: string }> = [
  { day: 1, short: "Mon", letter: "M" },
  { day: 2, short: "Tue", letter: "T" },
  { day: 3, short: "Wed", letter: "W" },
  { day: 4, short: "Thu", letter: "T" },
  { day: 5, short: "Fri", letter: "F" },
  { day: 6, short: "Sat", letter: "S" },
  { day: 7, short: "Sun", letter: "S" },
];
const ALL_DAYS = WEEKDAYS.map((w) => w.day);
export const WORKDAYS = [1, 2, 3, 4, 5];

/** Mirrors the server's caps (`MAX_TALENT_RULES`, `MAX_RULE_LINE_CHARS`). */
export const MAX_RULES = 12;
export const MAX_RULE_CHARS = 120;
const MAX_PROJECT_USD = 10_000_000;
const MAX_HOURLY_USD = 100_000;

export interface PreferencesDraft {
  minProject: string;
  minHourly: string;
  minMonthly: string;
  /** Selected ISO weekdays. All seven = every day (stored as nothing set). */
  days: number[];
  /** Off = "any time": no stated window, the default one applies. */
  hoursOn: boolean;
  hoursStart: number;
  hoursEnd: number;
  /** "" or YYYY-MM-DD. */
  availableFrom: string;
  rules: string[];
}

export type DraftField = "minProject" | "minHourly" | "minMonthly" | "days" | "hours" | "rules";

export type DraftResult =
  | { ok: true; body: SellerPreferencesUpdate }
  | { ok: false; errors: Partial<Record<DraftField, string>> };

const money = (usd: number | null | undefined): string => (usd ? String(usd) : "");

export function draftFromValues(v: SellerPreferenceValues | null | undefined): PreferencesDraft {
  const days = v?.working_days ?? [];
  const hasHours = v?.working_hours_start != null && v?.working_hours_end != null;
  return {
    minProject: money(v?.min_project_usd),
    minHourly: money(v?.min_hourly_usd),
    minMonthly: money(v?.min_monthly_usd),
    days: days.length > 0 ? [...days].sort((a, b) => a - b) : [...ALL_DAYS],
    hoursOn: hasHours,
    hoursStart: hasHours ? (v?.working_hours_start ?? 9) : 9,
    hoursEnd: hasHours ? (v?.working_hours_end ?? 18) : 18,
    availableFrom: v?.available_from ?? "",
    rules: [...(v?.rules ?? [])],
  };
}

/** A money field as whole dollars: "" -> null, "1,500" / "$1500" -> 1500, junk -> NaN. */
export function parseUsd(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  if (!/^\d+$/.test(cleaned)) return Number.NaN;
  const value = Number(cleaned);
  return value === 0 ? null : value;
}

export function updateFromDraft(d: PreferencesDraft): DraftResult {
  const errors: Partial<Record<DraftField, string>> = {};
  const read = (field: DraftField, raw: string, max: number): number | null => {
    const value = parseUsd(raw);
    if (value !== null && (Number.isNaN(value) || value > max)) {
      errors[field] = Number.isNaN(value) ? "Use whole dollars, like 500." : "That is too high.";
      return null;
    }
    return value;
  };
  const minProject = read("minProject", d.minProject, MAX_PROJECT_USD);
  const minHourly = read("minHourly", d.minHourly, MAX_HOURLY_USD);
  const minMonthly = read("minMonthly", d.minMonthly, MAX_PROJECT_USD);
  const days = [...new Set(d.days)].filter((n) => n >= 1 && n <= 7).sort((a, b) => a - b);
  if (days.length === 0) errors.days = "Pick at least one day you work.";
  if (d.hoursOn && !(d.hoursStart >= 0 && d.hoursStart < d.hoursEnd && d.hoursEnd <= 24)) {
    errors.hours = "Your day has to start before it ends.";
  }
  const rules = d.rules.map((r) => r.replace(/\s+/g, " ").trim()).filter(Boolean);
  if (rules.length > MAX_RULES) errors.rules = `Keep it to ${MAX_RULES} or fewer.`;
  else if (rules.some((r) => r.length > MAX_RULE_CHARS)) {
    errors.rules = `Keep each one under ${MAX_RULE_CHARS} characters.`;
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return {
    ok: true,
    body: {
      min_project_usd: minProject,
      min_hourly_usd: minHourly,
      min_monthly_usd: minMonthly,
      // A full week restricts nothing, and nothing is how the server stores it.
      working_days: days.length === 7 ? [] : days,
      working_hours_start: d.hoursOn ? d.hoursStart : null,
      working_hours_end: d.hoursOn ? d.hoursEnd : null,
      available_from: d.availableFrom || null,
      rules,
    },
  };
}

/** "Mon-Fri", "Every day", "Mon, Wed, Fri", "Mon-Wed, Sat". */
export function summarizeDays(days: number[]): string {
  const set = [...new Set(days)].filter((n) => n >= 1 && n <= 7).sort((a, b) => a - b);
  if (set.length === 0 || set.length === 7) return "Every day";
  const short = (n: number) => WEEKDAYS[n - 1].short;
  const runs: string[] = [];
  let start = set[0];
  let prev = set[0];
  for (const day of [...set.slice(1), Number.POSITIVE_INFINITY]) {
    if (day === prev + 1) {
      prev = day;
      continue;
    }
    if (prev - start >= 2) runs.push(`${short(start)}-${short(prev)}`);
    else if (prev === start) runs.push(short(start));
    else runs.push(short(start), short(prev));
    start = day;
    prev = day;
  }
  return runs.join(", ");
}

/** One labelled facet of the talent's preferences, as the page's left column shows it. */
export interface PreferenceSection {
  key: "rates" | "categories" | "when" | "start" | "skip";
  label: string;
  /** What the talent set, one line each; empty when they set nothing here. */
  values: string[];
  /** What an unset facet means, said plainly. */
  unset: string;
}

/** The talent's current preferences, facet by facet, for the left column: what is set, and
 *  what an unset facet means. The per-kind minimums appear only when Mira captured any. */
export function preferenceSections(
  v: SellerPreferenceValues | null | undefined,
): PreferenceSection[] {
  const rates: string[] = [];
  if (v?.min_project_usd) rates.push(`${formatUsd(v.min_project_usd)}+ per project`);
  if (v?.min_hourly_usd) rates.push(`${formatUsd(v.min_hourly_usd)}+ per hour`);
  if (v?.min_monthly_usd) rates.push(`${formatUsd(v.min_monthly_usd)}+ per month`);

  const days = v?.working_days ?? [];
  const hasHours = v?.working_hours_start != null && v?.working_hours_end != null;
  const when: string[] = [];
  if (days.length > 0 || hasHours) {
    const hours = hasHours
      ? `, ${formatHour(v?.working_hours_start as number)}-${formatHour(v?.working_hours_end as number)}`
      : "";
    when.push(`${days.length > 0 ? summarizeDays(days) : "Every day"}${hours}`);
  }

  const start: string[] = [];
  if (v?.available_from) {
    const at = new Date(`${v.available_from}T00:00:00`);
    if (!Number.isNaN(at.getTime())) {
      start.push(at.toLocaleDateString("en-US", { month: "short", day: "numeric" }));
    }
  }

  const sections: PreferenceSection[] = [
    { key: "rates", label: "Minimum rates", values: rates, unset: "No minimum yet" },
  ];
  const categories = v?.category_minimums ?? [];
  if (categories.length > 0) {
    sections.push({
      key: "categories",
      label: "By kind of work",
      values: categories.map((c) => `${c.label}: ${formatUsd(c.usd)}+`),
      unset: "",
    });
  }
  sections.push(
    { key: "when", label: "When you work", values: when, unset: "Any day, any time" },
    { key: "start", label: "Free to start", values: start, unset: "Now" },
    { key: "skip", label: "Work you skip", values: v?.rules ?? [], unset: "Nothing" },
  );
  return sections;
}
